/**
 * Types pour les Users
 */

export type UserRole =
  | 'super_admin'
  | 'tenant_admin'
  | 'company_admin'
  | 'manager_itc'
  | 'recruiter'
  | 'coordinator_tm'
  | 'contributor_tm'
  | 'knowledge_manager';

export type UserStatus = 'active' | 'inactive' | 'suspended';

export interface User {
  id: number;
  companyId: number;
  keycloakId: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  initials: string;
  phone: string | null;
  avatarUrl: string | null;
  role: UserRole;
  status: UserStatus;
  lastLoginAt: string | null;
  preferences: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface UserFilters {
  search?: string;
  role?: UserRole;
  status?: UserStatus;
  companyId?: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginationMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PaginatedUsers {
  data: User[];
  meta: PaginationMeta;
}

export interface CreateUserDto {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  phone?: string;
  companyId?: number;
  /** Mot de passe temporaire — si fourni, pas d'envoi d'email */
  temporaryPassword?: string;
}

export interface UpdateUserDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role?: UserRole;
  status?: UserStatus;
  companyId?: number;
}

/**
 * Configuration d'affichage des rôles
 */
export const USER_ROLE_CONFIG: Record<
  UserRole,
  { label: string; color: string; bgColor: string }
> = {
  super_admin: {
    label: 'Super Admin',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
  },
  tenant_admin: {
    label: 'Admin Tenant',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
  },
  company_admin: {
    label: 'Admin Société',
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-100',
  },
  manager_itc: {
    label: 'Manager ITC',
    color: 'text-teal-700',
    bgColor: 'bg-teal-100',
  },
  recruiter: {
    label: 'Recruteur',
    color: 'text-cyan-700',
    bgColor: 'bg-cyan-100',
  },
  coordinator_tm: {
    label: 'Coordinateur TM',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
  },
  contributor_tm: {
    label: 'Contributeur TM',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
  },
  knowledge_manager: {
    label: 'Knowledge Manager',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
  },
};

/**
 * Configuration d'affichage des statuts
 */
export const USER_STATUS_CONFIG: Record<
  UserStatus,
  { label: string; color: string; bgColor: string }
> = {
  active: {
    label: 'Actif',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  inactive: {
    label: 'Inactif',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
  },
  suspended: {
    label: 'Suspendu',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
};
